import {gql} from '@apollo/client';
import {captureException} from '@sentry/browser';
import React, {ErrorInfo, ReactNode} from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import PrintableWrapper from 'notebook/mediocre/print/PrintableWrapper';
import {errorIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  StudyDesignPrintViewQuery,
  StudyDesignPrintViewQueryVariables,
} from './__generated/StudyDesignPrintViewQuery';

export const STUDY_DESIGN_PRINT_VIEW_QUERY = gql`
  query StudyDesignPrintViewQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          ... on StudySchemaNode {
            id
            studyType
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Composite component that renders the study design in a printable format.
 *
 * This component can be shown to a user on its own page - it should not be nested in other components.
 * It is used for both user-initiated browser print and server-side headless Chrome rendering.
 *
 * Styling for the export is provided by Less-based CSS classes (study-design-export--*) defined in
 * less/study-design-export/study-design-export-styles.less, which are bundled into benchling.css for
 * the live UI and html-export.css for the second Playwright pass in headless_browser.py.
 *
 * - PrintableWrapper to block on nested resources loading (e.g. <img> tags) and resize wide content.
 * - ErrorBoundary to block that any rendering errors block server-side rendering jobs.
 * - InnerStudyDesignPrintView to fetch the study data and render the appropriate study adapter.
 */
export default function StudyDesignPrintView(props: {studyId: string}): JSX.Element {
  return (
    <PrintableWrapper title="Study Design">
      <ErrorBoundary>
        <InnerStudyDesignPrintView studyId={props.studyId} />
      </ErrorBoundary>
    </PrintableWrapper>
  );
}

/**
 * Fetches the minimal study data to then dispatch the appropriate study adapter for rendering.
 *
 * This component notably uses proper error handling and loading states to ensure that if the study design
 * is being rendered in a server-side context, the data is captured when it is fully loaded.
 */
function InnerStudyDesignPrintView(props: {studyId: string}): JSX.Element {
  const result = useBenchlingQuery<StudyDesignPrintViewQuery, StudyDesignPrintViewQueryVariables>(
    STUDY_DESIGN_PRINT_VIEW_QUERY,
    {variables: {studyId: props.studyId}}
  );
  switch (result.type) {
    case QueryResultType.PENDING:
      return <CenteredHorizontalThrobber />;
    case QueryResultType.SUCCESS: {
      if (isInaccessible(result.data.study)) {
        return (
          <ErrorBannerWithIndicator reason="inaccessible" queryResultType={result.type}>
            The study design is inaccessible.
          </ErrorBannerWithIndicator>
        );
      }
      const {studyType} = result.data.study.studySchema;
      const adapter = getTypedStudyAdapter(studyType);
      const {tab: StudyDesignTabContents} = adapter.design.ui;
      if (!StudyDesignTabContents) {
        return (
          <ErrorBannerWithIndicator
            reason="no-study-design-for-adapter"
            queryResultType={result.type}
            studyType={studyType}
          >
            The study design could not be displayed.
          </ErrorBannerWithIndicator>
        );
      }
      return <StudyDesignTabContents studyId={result.data.study.id} locked={true} viewer={true} />;
    }
    default:
      return (
        <ErrorBannerWithIndicator reason="network" queryResultType={result.type}>
          An error occurred while loading the study design.
        </ErrorBannerWithIndicator>
      );
  }
}

/**
 * A user-readable and machine-readable error banner.
 *
 * This component should be used anytime an error occurs when rendering the study design to ensure that any
 * server-side rendering jobs fail. {@link errorIndicatorAttrs} can be used for cases where the banner is not
 * appropriate.
 *
 * @param props.queryResultType - the graphql query result type. Captured in the HTML for debugging.
 * @param props.studyType - the type of the study that caused the error. Captured in the HTML for debugging.
 * @param props.reason - the reason for the error. Captured in the HTML for debugging.
 */
export function ErrorBannerWithIndicator(props: {
  queryResultType?: QueryResultType;
  studyType?: StudyType;
  reason: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      // Signal that an error occurred.
      {...errorIndicatorAttrs()}
      // Attach some metadata in case we are able to capture the HTML for debugging.
      data-study-design-query-result-type={props.queryResultType}
      data-study-design-type={props.studyType}
      data-error-reason={props.reason}
      className="study-design-export--error-banner"
    >
      <InlineBanner bannerType={InlineBannerType.ERROR} text={props.children} />
    </div>
  );
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary that captures errors and displays an error banner, with an error indicator.
 *
 * This component is necessary to ensure that a crash at render time in a child component does not simply
 * render nothing, but instead an error indicator that will fail any server-side rendering jobs.
 *
 * If we were to capture this failed HTML/PDF, the visual error indicator would be useful for debugging.
 *
 * TODO(BNCH-107024): It would be nice to have a positive indicator for when the study design is fully loaded,
 * so that this component serves primarily as a UX enhancement, esp. since a crash above this component
 * would still result in a blank page.
 */
class ErrorBoundary extends React.Component {
  public override state: ErrorBoundaryState = {hasError: false};

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return {hasError: true};
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error, {extra: {errorInfo}});
  }

  override render(): ReactNode {
    return this.state.hasError ? (
      <ErrorBannerWithIndicator reason="js">
        An error occurred while rendering the study design.
      </ErrorBannerWithIndicator>
    ) : (
      this.props.children
    );
  }
}
